package org.catharsis.spendtel.feature.presentation.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material.icons.outlined.CreditCard
import androidx.compose.material.icons.outlined.DeleteForever
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.SupportAgent
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.catharsis.spendtel.feature.data.BillingEntitlement
import org.catharsis.spendtel.feature.data.creditsSummaryLine
import org.catharsis.spendtel.feature.data.normalizePriceLabel
import org.catharsis.spendtel.feature.data.totalAvailableReports
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors

@Composable
internal fun SettingsBillingCard(
    entitlement: BillingEntitlement?,
    isPurchasing: Boolean,
    onBuyReport: () -> Unit,
    modifier: Modifier = Modifier,
    buyButtonFullWidth: Boolean = true,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
    ) {
        SettingsBillingSection(
            entitlement = entitlement,
            isPurchasing = isPurchasing,
            onBuyReport = onBuyReport,
            buyButtonFullWidth = buyButtonFullWidth,
            modifier = Modifier.padding(18.dp),
        )
    }
}

@Composable
internal fun SettingsBillingSection(
    entitlement: BillingEntitlement?,
    isPurchasing: Boolean,
    onBuyReport: () -> Unit,
    modifier: Modifier = Modifier,
    buyButtonFullWidth: Boolean = true,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = Icons.Outlined.CreditCard,
                contentDescription = null,
                tint = SpendTelReportColors.indigo,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = "Report credits",
                    style = MaterialTheme.typography.labelLarge,
                    color = SpendTelReportColors.inkMuted,
                )
                if (entitlement == null) {
                    CircularProgressIndicator(
                        modifier = Modifier.padding(top = 8.dp),
                        color = SpendTelReportColors.violet,
                        strokeWidth = 2.dp,
                    )
                } else {
                    Text(
                        text = entitlement.creditsSummaryLine(),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = SpendTelReportColors.ink,
                    )
                }
            }
        }

        if (entitlement != null) {
            HorizontalDivider(color = SpendTelReportColors.inkMuted.copy(alpha = 0.15f))

            SettingsBillingRow(
                label = "Available now",
                value = entitlement.totalAvailableReports().toString(),
            )
            if (entitlement.freeReportAllowance > 0) {
                SettingsBillingRow(
                    label = "Free reports remaining",
                    value = entitlement.freeReportsRemaining.toString(),
                )
            }
            SettingsBillingRow(
                label = "Purchased credits",
                value = entitlement.paidCredits.toString(),
            )
            SettingsBillingRow(
                label = "Reports created",
                value = entitlement.reportsCreated.toString(),
            )

            val price = normalizePriceLabel(entitlement.priceLabel)
            Button(
                onClick = onBuyReport,
                enabled = !isPurchasing,
                modifier = if (buyButtonFullWidth) {
                    Modifier.fillMaxWidth()
                } else {
                    Modifier
                },
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = SpendTelReportColors.indigo,
                    contentColor = Color.White,
                ),
            ) {
                Text(
                    text = if (isPurchasing) "Processing…" else "Buy another report · $price",
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                )
            }
        }
    }
}

@Composable
internal fun SettingsBillingRow(
    label: String,
    value: String,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.inkMuted,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            color = SpendTelReportColors.ink,
        )
    }
}

@Composable
internal fun SettingsSupportCard(
    onContactSupport: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        onClick = onContactSupport,
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
    ) {
        SettingsSupportRow(
            modifier = Modifier.padding(18.dp),
        )
    }
}

@Composable
internal fun SettingsSupportRow(
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.SupportAgent,
            contentDescription = null,
            tint = SpendTelReportColors.indigo,
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = "Contact support",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = SpendTelReportColors.ink,
            )
            Text(
                text = "Questions about billing, your account, or reports? Send us a quick note.",
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.inkMuted,
                lineHeight = MaterialTheme.typography.bodyMedium.lineHeight,
            )
        }
        Icon(
            imageVector = Icons.Outlined.ChevronRight,
            contentDescription = null,
            tint = SpendTelReportColors.inkMuted,
        )
    }
}

@Composable
internal fun SettingsGoalCard(
    goalTitle: String,
    onChangeGoal: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        onClick = onChangeGoal,
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
    ) {
        SettingsGoalRow(
            goalTitle = goalTitle,
            modifier = Modifier.padding(18.dp),
        )
    }
}

@Composable
internal fun SettingsGoalRow(
    goalTitle: String,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.Flag,
            contentDescription = null,
            tint = SpendTelReportColors.violet,
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = "Financial goal",
                style = MaterialTheme.typography.labelLarge,
                color = SpendTelReportColors.inkMuted,
            )
            Text(
                text = goalTitle,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = SpendTelReportColors.ink,
            )
        }
        Icon(
            imageVector = Icons.Outlined.ChevronRight,
            contentDescription = null,
            tint = SpendTelReportColors.inkMuted,
        )
    }
}

@Composable
internal fun SettingsDeleteAccountCard(
    isDeleting: Boolean,
    onDeleteAccount: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
    ) {
        SettingsDeleteAccountSection(
            isDeleting = isDeleting,
            onDeleteAccount = onDeleteAccount,
            modifier = Modifier.padding(18.dp),
        )
    }
}

@Composable
internal fun SettingsDeleteAccountSection(
    isDeleting: Boolean,
    onDeleteAccount: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = Icons.Outlined.DeleteForever,
                contentDescription = null,
                tint = SpendTelReportColors.rose,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = "Delete account",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                )
                Text(
                    text = "Permanently remove your account, spending reports, and uploaded bank statements.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                    lineHeight = MaterialTheme.typography.bodyMedium.lineHeight,
                )
            }
        }

        OutlinedButton(
            onClick = onDeleteAccount,
            enabled = !isDeleting,
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(10.dp),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = SpendTelReportColors.rose,
            ),
        ) {
            Text(
                text = if (isDeleting) "Deleting account…" else "Delete account and data",
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            )
        }
    }
}

@Composable
internal fun DeleteAccountDialog(
    isDeleting: Boolean,
    onDismiss: () -> Unit,
    onConfirmDelete: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = {
            if (!isDeleting) onDismiss()
        },
        icon = {
            Icon(
                imageVector = Icons.Outlined.DeleteForever,
                contentDescription = null,
                tint = SpendTelReportColors.rose,
            )
        },
        title = {
            Text(
                text = "Delete your account?",
                fontWeight = FontWeight.SemiBold,
            )
        },
        text = {
            Text(
                text = "This permanently deletes your SpendTel account, all spending reports, and every uploaded bank statement. This action cannot be undone.",
                style = MaterialTheme.typography.bodyMedium,
            )
        },
        confirmButton = {
            TextButton(
                onClick = onConfirmDelete,
                enabled = !isDeleting,
            ) {
                Text(
                    text = if (isDeleting) "Deleting…" else "Delete everything",
                    color = SpendTelReportColors.rose,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                enabled = !isDeleting,
            ) {
                Text("Cancel")
            }
        },
    )
}
